/*!
 * TinyPoker 級別詳情頁面
 * 展示特定級別的真人 vs EcoBot 數據對比
 */
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Alert,
  Badge,
  ButtonGroup,
  Input,
  Label,
  FormGroup
} from "reactstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import axios from "axios";
import { Line } from "react-chartjs-2";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import TinyWeekRangePicker from "../../components/tiny/TinyWeekRangePicker";
import GrowthBadge from "../../components/tiny/GrowthBadge";
import { windowNav, previousWindow } from "../../components/tiny/weekWindowUtils";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import TinyPageHeader from "components/tiny/TinyPageHeader";
import { GAME_TYPES, GAME_TYPE_KEYS } from "constant/gameTypes";
import { COLORS, inputStyle, labelStyle } from "constant/adminTheme";

// 與「級別 Top 玩家」同值 —— 兩頁的查詢鈕要長一樣
const primaryBtnStyle = (disabled) => ({
  background: disabled ? 'rgba(102,126,234,0.5)' : COLORS.accentGradient,
  border: 'none',
  padding: '10px 16px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  boxShadow: disabled ? 'none' : '0 4px 12px rgba(102,126,234,0.3)',
  marginBottom: 0,
  whiteSpace: 'nowrap',
  minWidth: '92px',
});
// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_BASE = "/api/tiny";

// 數字格式化
const numberWithCommas = (num) => {
  if (num === null || num === undefined) return "0";
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// 格式化為 K/M
const formatKM = (value) => {
  if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
  if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
  return Math.round(value).toString();
};

// 遊戲類型配置
// 四種遊戲類別的單一來源（見 constant/gameTypes.js 的說明）
const gameTypeConfig = GAME_TYPES;

function TinyGameLevelDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  // URL 參數
  const urlCategory = queryParams.get('category') || '';
  const urlSb = queryParams.get('sb') ? parseFloat(queryParams.get('sb')) : null;
  const urlBb = queryParams.get('bb') ? parseFloat(queryParams.get('bb')) : null;
  // 注意：ante 可能是 "0"，也要正确解析
  const urlAnte = queryParams.get('ante') !== null ? parseFloat(queryParams.get('ante')) : null;
  const weeksParam = queryParams.get('weeks') || '';
  const initialWeeks = weeksParam.split(',').filter(w => w);
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);  // 上一期同級別（供 Rake 期比較）
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState(initialWeeks);
  const [appliedWeeks, setAppliedWeeks] = useState(initialWeeks);
  const [trendMetrics, setTrendMetrics] = useState({
    rake: false,
    pl: false,
    preRakePl: false,
    jpFee: false,
    jpPayout: false,
    preRakePlExcludeJp: true  // 默认显示水前输赢扣除JP
  });
  
  // 搜索表单 State
  // ⚠️ 遊戲類別預設 NLH（水量最大宗）—— 目的是讓空手進來的人
  //    「只需要按一次查詢」，而不是先做三個選擇。
  //    🚫 **盲注級別刻意不預設**：那是這一頁的主題，替使用者選了等於
  //       幫他決定他要看什麼，猜錯還得重選重查。
  //    週期由 loadAvailableWeeks 帶最新一週（本來就有）。
  const [searchForm, setSearchForm] = useState({
    category: urlCategory || 'NLH',
    sb: urlSb || '',
    bb: urlBb || '',
    ante: urlAnte || ''
  });
  
  // 可用级别列表
  const [availableLevels, setAvailableLevels] = useState([]);
  const [loadingLevels, setLoadingLevels] = useState(false);
  
  // 应用周期选择
  const applyWeekSelection = () => {
    if (selectedWeeks.length === 0) return;
    setAppliedWeeks([...selectedWeeks]);
  };
  
  // 是否有有效的搜索参数
  const hasValidParams = urlCategory && !isNaN(urlSb) && !isNaN(urlBb);
  // 🔑 tab 停在哪個類別，跟「畫面上那份資料屬於哪個類別」是**兩件事**。
  //    先前 `currentCategory` 直接取 `data.level_info.category`，所以只要
  //    某個類別查不到資料就沒地方記錄「使用者現在點在哪個 tab」，
  //    只能退回初始畫面（Brad 2026-09-10 點 Others 時遇到）。
  const [viewCategory, setViewCategory] = useState(null);
  // 該類別在所選週期沒有任何級別 → 留在結果版面、顯示警示，**不清空 data**，
  // 這樣切回原本的類別可以立刻還原（不用重查）。
  const [emptyCategory, setEmptyCategory] = useState(null);

  const hasSearched = data !== null;
  
  // 級別字符串
  const getLevelString = () => {
    const sb = searchForm.sb || urlSb;
    const bb = searchForm.bb || urlBb;
    const ante = searchForm.ante || urlAnte;
    if (ante) return `${sb}/${bb} (${ante})`;
    return `${sb}/${bb}`;
  };
  
  // 加載可用週期
  useEffect(() => {
    loadAvailableWeeks();
  }, []);
  
  // 如果有 URL 参数，自动加载数据
  useEffect(() => {
    if (hasValidParams && selectedWeeks.length > 0) {
      loadData(urlCategory, urlSb, urlBb, urlAnte);
    }
  }, []);
  
  const loadAvailableWeeks = async () => {
    try {
      const response = await axios.get(`${API_BASE}/game-stats/weeks`);
      if (response.data.code === 1) {
        const weeksList = response.data.data.weeks || [];
        setAvailableWeeks(weeksList);
        // 如果没有选择周期，默认选择最新的一周
        if (selectedWeeks.length === 0 && weeksList.length > 0) {
          setSelectedWeeks([weeksList[0]]);
        }
      }
    } catch (err) {
      console.error('Failed to load weeks:', err);
    }
  };
  
  // 加载可用级别
  const loadAvailableLevels = async (category, weeks) => {
    if (!category || !weeks || weeks.length === 0) {
      setAvailableLevels([]);
      return;
    }
    
    setLoadingLevels(true);
    try {
      const response = await axios.get(`${API_BASE}/level-detail/levels`, {
        params: {
          category,
          weeks: weeks.join(',')
        }
      });
      
      const levels = response.data.success ? (response.data.levels || []) : [];
      setAvailableLevels(levels);
      // ⚠️ 除了設 state 也把清單**回傳** —— switchCategory 需要當場知道
      //    新類別有哪些級別才能挑一個接著查，等 state 更新來不及。
      return levels;
    } catch (err) {
      console.error('Failed to load levels:', err);
      setAvailableLevels([]);
      return [];
    } finally {
      setLoadingLevels(false);
    }
  };
  
  const loadData = async (category, sb, bb, ante, weeksOverride) => {
    const wk = (weeksOverride && weeksOverride.length) ? weeksOverride : selectedWeeks;
    if (!category || isNaN(sb) || isNaN(bb) || wk.length === 0) {
      setError('請填寫完整的搜索條件');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        weeks: wk.join(','),
        category,
        sb,
        bb
      };
      
      // 注意：ante 可能是 0，也要传递
      if (ante !== null && ante !== undefined) params.ante = ante;
      
      const response = await axios.get(`${API_BASE}/level-detail`, { params });
      
      if (response.data.success) {
        setData(response.data.data);
        setViewCategory(category);
        setEmptyCategory(null);
        setSelectedWeeks(wk);
        setAppliedWeeks([...wk]);

        // 更新 URL
        const urlParams = new URLSearchParams({
          category,
          sb,
          bb,
          weeks: wk.join(',')
        });
        // 注意：ante 可能是 0，也要传递
        if (ante !== null && ante !== undefined) urlParams.append('ante', ante);
        navigate(`?${urlParams.toString()}`, { replace: true });
      } else {
        setError(response.data.error || '加載失敗');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // 上一期（前 N 週）同級別，供「與上一期比較」
  const fetchPrevData = async () => {
    if (!data || !appliedWeeks.length) { setPrevData(null); return; }
    const prev = previousWindow(availableWeeks, appliedWeeks);
    if (!prev || !prev.length) { setPrevData(null); return; }
    const li = data.level_info;
    const params = { weeks: prev.join(','), category: li.category, sb: li.sb, bb: li.bb };
    if (li.ante !== null && li.ante !== undefined) params.ante = li.ante;
    try {
      const res = await axios.get(`${API_BASE}/level-detail`, { params });
      setPrevData(res.data.success ? res.data.data : null);
    } catch (e) {
      setPrevData(null);
    }
  };

  useEffect(() => {
    fetchPrevData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedWeeks, data]);

  // 上/下 N 週：套用移動後視窗並重載當前級別（免再送出）
  const applyWeekWindow = (win) => {
    if (!win || !win.length || !data) return;
    loadData(data.level_info.category, data.level_info.sb, data.level_info.bb, data.level_info.ante, win);
  };

  // 搜索按钮处理
  const handleSearch = () => {
    // 直接使用 searchForm 中的 ante
    const ante = searchForm.ante !== '' ? parseFloat(searchForm.ante) : null;
    
    loadData(
      searchForm.category, 
      parseFloat(searchForm.sb), 
      parseFloat(searchForm.bb), 
      ante
    );
  };
  
  // 监听类别和周期变化，加载可用级别
  useEffect(() => {
    if (searchForm.category && selectedWeeks.length > 0) {
      loadAvailableLevels(searchForm.category, selectedWeeks);
    } else {
      setAvailableLevels([]);
      setSearchForm(prev => ({ ...prev, sb: '', bb: '', ante: '' }));
    }
  }, [searchForm.category, selectedWeeks]);
  
  // 趨勢圖表數據
  const generateTrendChartData = () => {
    if (!data || !data.by_week) return null;
    
    const weeks = Object.keys(data.by_week).sort();
    const datasets = [];
    
    if (trendMetrics.rake) {
      datasets.push({
        label: '真人 Rake',
        data: weeks.map(w => data.by_week[w].real.rake),
        borderColor: '#1f8ef1',
        backgroundColor: 'rgba(31, 142, 241, 0.1)',
        tension: 0.4
      });
      datasets.push({
        label: 'EcoBot Rake',
        data: weeks.map(w => data.by_week[w].ecobot.rake),
        borderColor: '#00d6b4',
        backgroundColor: 'rgba(0, 214, 180, 0.1)',
        tension: 0.4,
        borderDash: [5, 5]
      });
    }
    
    if (trendMetrics.pl) {
      datasets.push({
        label: '真人 P&L',
        data: weeks.map(w => data.by_week[w].real.pl),
        borderColor: '#fd5d93',
        backgroundColor: 'rgba(253, 93, 147, 0.1)',
        tension: 0.4
      });
      datasets.push({
        label: 'EcoBot P&L',
        data: weeks.map(w => data.by_week[w].ecobot.pl),
        borderColor: '#f17e5d',
        backgroundColor: 'rgba(241, 126, 93, 0.1)',
        tension: 0.4,
        borderDash: [5, 5]
      });
    }
    
    if (trendMetrics.preRakePl) {
      datasets.push({
        label: '真人水前輸贏',
        data: weeks.map(w => data.by_week[w].real.pre_rake_pl),
        borderColor: '#1f8ef1',
        backgroundColor: 'rgba(31, 142, 241, 0.2)',
        tension: 0.4
      });
      datasets.push({
        label: 'EcoBot 水前輸贏',
        data: weeks.map(w => data.by_week[w].ecobot.pre_rake_pl),
        borderColor: '#00d6b4',
        backgroundColor: 'rgba(0, 214, 180, 0.2)',
        tension: 0.4,
        borderDash: [5, 5]
      });
    }
    
    if (trendMetrics.jpFee) {
      datasets.push({
        label: '真人 JP FEE',
        data: weeks.map(w => data.by_week[w].real.jp_fee),
        borderColor: '#ffa726',
        backgroundColor: 'rgba(255, 167, 38, 0.1)',
        tension: 0.4
      });
      datasets.push({
        label: 'EcoBot JP FEE',
        data: weeks.map(w => data.by_week[w].ecobot.jp_fee),
        borderColor: '#ff7043',
        backgroundColor: 'rgba(255, 112, 67, 0.1)',
        tension: 0.4,
        borderDash: [5, 5]
      });
    }
    
    if (trendMetrics.jpPayout) {
      datasets.push({
        label: '真人 JP PAYOUT',
        data: weeks.map(w => data.by_week[w].real.jp_payout),
        borderColor: '#66bb6a',
        backgroundColor: 'rgba(102, 187, 106, 0.1)',
        tension: 0.4
      });
      datasets.push({
        label: 'EcoBot JP PAYOUT',
        data: weeks.map(w => data.by_week[w].ecobot.jp_payout),
        borderColor: '#81c784',
        backgroundColor: 'rgba(129, 199, 132, 0.1)',
        tension: 0.4,
        borderDash: [5, 5]
      });
    }
    
    if (trendMetrics.preRakePlExcludeJp) {
      datasets.push({
        label: '真人水前輸贏扣除JP',
        data: weeks.map(w => data.by_week[w].real.pre_rake_pl_exclude_jp),
        borderColor: '#9c27b0',
        backgroundColor: 'rgba(156, 39, 176, 0.1)',
        tension: 0.4,
        borderWidth: 3
      });
      datasets.push({
        label: 'EcoBot 水前輸贏扣除JP',
        data: weeks.map(w => data.by_week[w].ecobot.pre_rake_pl_exclude_jp),
        borderColor: '#ba68c8',
        backgroundColor: 'rgba(186, 104, 200, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        borderDash: [5, 5]
      });
    }
    
    return {
      labels: weeks,
      datasets
    };
  };
  
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { color: '#fff' }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: $${numberWithCommas(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#fff',
          callback: (value) => '$' + formatKM(value)
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      x: {
        ticks: { color: '#fff' },
        grid: { color: 'rgba(255,255,255,0.1)' }
      }
    }
  };
  
  // 計算佔比
  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return '0.0';
    return ((value / total) * 100).toFixed(1);
  };
  
  // 渲染
  // 還沒有任何結果時整頁等；已經有結果（切級別 / 切類別 / 換週）走下面的遮罩，舊資料留著
  if (loading && !hasSearched) {
    return (
      <div className="content">
        <TinyLoading text="載入級別詳情" />
      </div>
    );
  }
  
  // 如果没有数据，显示搜索面板
  if (!hasSearched) {
    return (
      <div className="content">
      {/* 這頁是從「遊戲種類數據」鑽進來的。⚠️ 原本上面有一條麵包屑，
          Brad 2026-09-10 要求移除 —— 側欄已經標出所在位置，麵包屑是重複的。 */}
      <TinyPageHeader
        title="級別詳情"
        description="單一級別的桌數、手數、Rake 與玩家表現。"
      />
        
        {/* ⚠️ 這張查詢面板本來跟「級別 Top 玩家」長得完全不一樣：
               標題卡 + 兩排各自對齊的欄位 + 一顆置中的 25px 圓角漸層大鈕，
               而 Top 玩家是一排等高的 FormGroup 加右邊一顆查詢。
               兩頁做的是同一件事（選週期＋選級別 → 查），所以統一成 Top 玩家那個版型
               （Brad 2026-09-10）。
            🔑 查詢鈕那一欄要有**隱形 Label 佔位**，否則它會比同列輸入框高一截 ——
               這個 repo 的 design pattern 筆記早就記過，這頁先前沒照做。 */}
        <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
          <CardBody style={{ padding: '20px' }}>
            <Row>
              <Col xs={12} md={6} xl={4}>
                <FormGroup>
                  <Label style={labelStyle}><i className="fa fa-calendar mr-2" />選擇週期</Label>
                  {/* 還沒查詢過，沒有「目前的視窗」可以前後移 → nav 留空 */}
                  <TinyWeekRangePicker
                    options={availableWeeks}
                    selectedValues={selectedWeeks}
                    onChange={setSelectedWeeks}
                    onApply={applyWeekSelection}
                    onCancel={() => setSelectedWeeks([...appliedWeeks])}
                    block
                    dropdownAlign="left"
                    dropdownMinWidth={360}
                    dropdownMaxWidth={560}
                  />
                </FormGroup>
              </Col>

              <Col xs={6} md={6} xl={2}>
                <FormGroup>
                  <Label style={labelStyle}><i className="fa fa-gamepad mr-2" />遊戲類別</Label>
                  <Input
                    type="select"
                    value={searchForm.category}
                    onChange={(e) => setSearchForm({ category: e.target.value, sb: '', bb: '', ante: '' })}
                    style={inputStyle}
                  >
                    <option value="">請選擇...</option>
                    {GAME_TYPE_KEYS.map((k) => (
                      <option key={k} value={k}>{GAME_TYPES[k].name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col xs={12} md={6} xl={4}>
                <FormGroup>
                  <Label style={labelStyle}>
                    <i className="fa fa-layer-group mr-2" />盲注級別
                    {loadingLevels && <TinyBusy style={{ marginLeft: 8 }} />}
                  </Label>
                  <Input
                    type="select"
                    value={searchForm.sb && searchForm.bb
                      ? `${searchForm.sb}_${searchForm.bb}_${searchForm.ante !== undefined && searchForm.ante !== null && searchForm.ante !== '' ? searchForm.ante : ''}`
                      : ''}
                    onChange={(e) => {
                      if (!e.target.value) {
                        setSearchForm({ ...searchForm, sb: '', bb: '', ante: '' });
                        return;
                      }
                      const [sb, bb, ante] = e.target.value.split('_');
                      setSearchForm({ ...searchForm, sb, bb, ante: ante || '' });
                    }}
                    disabled={!searchForm.category || selectedWeeks.length === 0 || loadingLevels}
                    style={inputStyle}
                  >
                    <option value="">
                      {!searchForm.category
                        ? '請先選擇遊戲類別...'
                        : selectedWeeks.length === 0
                        ? '請先選擇週期...'
                        : loadingLevels
                        ? '載入中...'
                        : availableLevels.length === 0
                        ? '無可用級別'
                        : '請選擇盲注級別...'}
                    </option>
                    {availableLevels.map((level, idx) => (
                      <option key={idx} value={`${level.sb}_${level.bb}_${(level.ante !== undefined && level.ante !== null ? level.ante : '')}`}>
                        {level.display}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col xs={12} md={12} xl={2}>
                <FormGroup>
                  {/* 佔位 label：讓按鈕與同列輸入框底線對齊 */}
                  <Label style={{ ...labelStyle, visibility: 'hidden', display: 'block' }}>查詢</Label>
                  <Button
                    block
                    onClick={handleSearch}
                    disabled={!searchForm.category || !searchForm.sb || !searchForm.bb || selectedWeeks.length === 0}
                    style={primaryBtnStyle(!searchForm.category || !searchForm.sb || !searchForm.bb || selectedWeeks.length === 0)}
                  >
                    <i className="fa fa-search mr-2" />查詢
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {error && (
          <Card style={{ background: COLORS.cardBg, border: `1px solid rgba(245,54,92,0.5)`, marginBottom: '20px' }}>
            <CardBody style={{ color: COLORS.danger, padding: '16px 20px' }}>
              <i className="fa fa-times-circle mr-2" />{error}
            </CardBody>
          </Card>
        )}

        {/* 空狀態卡：跟「級別 Top 玩家」同一個版型與語氣 */}
        {!error && (
          <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
            <CardBody className="text-center" style={{ color: COLORS.textMuted, padding: '48px 16px' }}>
              <i className="fa fa-magnifying-glass fa-2x mb-3" style={{ display: 'block', color: COLORS.textDimmed }} />
              選好週期、遊戲類別與盲注級別後，按「查詢」開始計算。
            </CardBody>
          </Card>
        )}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="content">
        <Alert color="danger">
          <strong>錯誤：</strong> {error}
        </Alert>
        <Button color="primary" onClick={() => {
          setData(null);
          setError(null);
        }}>
          <i className="fa fa-arrow-left mr-2" />
          返回搜索
        </Button>
      </div>
    );
  }
  
  const { summary } = data;
  const prevSummary = prevData?.summary || null;
  const levelNav = windowNav(availableWeeks, appliedWeeks);
  // tab 顯示的類別（可能是一個沒有資料的類別）；data 仍是上一次查到的那份
  const currentCategory = viewCategory || data.level_info.category;
  
  // 获取当前级别在列表中的索引
  const currentLevelIndex = availableLevels.findIndex(
    level => level.sb === data.level_info.sb && 
             level.bb === data.level_info.bb &&
             level.ante === data.level_info.ante
  );
  
  // 切换到上一个/下一个级别
  const switchLevel = (direction) => {
    if (availableLevels.length === 0) return;
    
    let newIndex = currentLevelIndex + direction;
    if (newIndex < 0) newIndex = availableLevels.length - 1;
    if (newIndex >= availableLevels.length) newIndex = 0;
    
    const newLevel = availableLevels[newIndex];
    loadData(currentCategory, newLevel.sb, newLevel.bb, newLevel.ante);
  };
  
  // 切换游戏类别
  //
  // 🔴 舊版是 `setData(null)` —— 那會讓 `hasSearched` 變 false，整頁**跳回
  //    初始查詢面板**（Brad 2026-09-10 回報）。在遊戲類別還是結果卡裡的
  //    ButtonGroup 時，那個行為的意思是「換類別了，請重挑級別」；但它現在是
  //    header 上的頁面 tab，切 tab 卻把畫面清空，讀起來就是頁面自己重置了。
  //
  // 現在改成**直接查新類別的對應級別**，維持在結果畫面：
  //   ① 新類別有一模一樣的盲注（sb/bb，含 ante）→ 用它
  //   ② 沒有 → 取 bb 最接近的那一個（10/20 → 換類別後可能只有 10/25）
  //   ③ 新類別完全沒有級別 → 只能退回查詢面板，並說明原因
  const switchCategory = async (newCategory) => {
    if (newCategory === currentCategory) return;

    const levels = await loadAvailableLevels(newCategory, selectedWeeks);

    if (!levels || levels.length === 0) {
      // 🚫 **不要 setData(null)** —— 那會讓 hasSearched 變 false 而跳回初始畫面。
      //    留在結果版面、tab 停在這個類別、只把數據區換成警示；
      //    `data` 原封不動，所以點回原本的類別立刻還原，不必重查。
      setViewCategory(newCategory);
      setEmptyCategory(newCategory);
      setSearchForm({ category: newCategory, sb: '', bb: '', ante: '' });
      setError(null);
      return;
    }

    const cur = data && data.level_info ? data.level_info : null;
    let target = null;
    if (cur) {
      const sameAnte = (lv) =>
        (lv.ante === undefined || lv.ante === null ? null : Number(lv.ante)) ===
        (cur.ante === undefined || cur.ante === null ? null : Number(cur.ante));
      target = levels.find((lv) => Number(lv.sb) === Number(cur.sb)
                                && Number(lv.bb) === Number(cur.bb)
                                && sameAnte(lv))
            || levels.find((lv) => Number(lv.sb) === Number(cur.sb)
                                && Number(lv.bb) === Number(cur.bb))
            // bb 最接近的那一個
            || levels.slice().sort((a, b) =>
                 Math.abs(Number(a.bb) - Number(cur.bb)) - Math.abs(Number(b.bb) - Number(cur.bb))
               )[0];
    }
    target = target || levels[0];

    setError(null);
    setSearchForm({
      category: newCategory,
      sb: target.sb,
      bb: target.bb,
      ante: target.ante !== undefined && target.ante !== null ? target.ante : '',
    });
    loadData(newCategory, Number(target.sb), Number(target.bb),
             target.ante !== undefined && target.ante !== null ? Number(target.ante) : null);
  };
  
  return (
    <div className="content">
      <TinyLoadingOverlay fixed active={loading} text="載入級別詳情">
      {/* 這頁是從「遊戲種類數據」鑽進來的。⚠️ 原本上面有一條麵包屑，
          Brad 2026-09-10 要求移除 —— 側欄已經標出所在位置，麵包屑是重複的。 */}
      <TinyPageHeader
        title="級別詳情"
        description="單一級別的桌數、手數、Rake 與玩家表現。"
        /* 空類別時不掛 badge —— 那個盲注屬於上一個類別，掛著會誤導 */
        badges={emptyCategory ? [] : [{ text: data.level_info.blinds_str, tone: "info" }]}
        scope={
          <TinyWeekRangePicker
            options={availableWeeks}
            selectedValues={selectedWeeks}
            onChange={setSelectedWeeks}
            onApply={() => {
              applyWeekSelection();
              if (data && selectedWeeks.length > 0) {
                loadData(
                  data.level_info.category,
                  data.level_info.sb,
                  data.level_info.bb,
                  data.level_info.ante
                );
              }
            }}
            onCancel={() => setSelectedWeeks([...appliedWeeks])}
            nav={levelNav}
            onWindowChange={applyWeekWindow}
          />
        }
        tabs={{
          items: GAME_TYPE_KEYS.map((cat) => ({
            key: cat,
            label: gameTypeConfig[cat].name,
            icon: gameTypeConfig[cat].icon,
          })),
          active: currentCategory,
          onChange: switchCategory,
        }}
      />
      
      {/* ⚠️ 這一整區在 v2.35.0 前是全站唯一一套自己的樣式：紫色漸層卡
             （`linear-gradient(#1e1e2e → #2d2d44)`，別頁都沒有）、reactstrap
             `ButtonGroup` 當遊戲類別切換、三張 black-dashboard 舊主題的
             `card-stats`。Brad 2026-09-10：「跟現在的風格不太一樣…太醜、太怪」。
          → 收斂成跟其他頁一致的三段結構：
             ① 遊戲類別 = 同一份資料的不同視角 → **公版 tab，進 header**
             ② 週期 + 上/下 N 週 = 換哪一份資料 → **header 的 scope**
                （跟遊戲種類數據、EcoBot 同一個寫法）
             ③ 盲注級別（‹ 選單 ›）與重新搜尋 = 這份資料怎麼切／動作
                → header 下方的控制列，不進 header
          ⚠️ 三張摘要卡改用雙週獎池那套 PoolPanel 版型（平面底 + 頂端一條
             類別色），**每一個數字、成長率與底下的玩家/桌/手都保留**。 */}

      {/* 控制列：盲注級別（可用 ‹ › 逐級切換）＋ 重新搜尋 */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        marginBottom: 16,
      }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
          <i className="fa fa-layer-group mr-2" />盲注級別
        </span>
        <Button
          size="sm"
          onClick={() => switchLevel(-1)}
          disabled={availableLevels.length === 0}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
                   color: '#fff', borderRadius: 6, padding: '6px 11px' }}
          title="上一個級別"
        >
          <i className="fa fa-chevron-left" />
        </Button>
        <Input
          type="select"
          value={emptyCategory
            ? ''
            : `${data.level_info.sb}_${data.level_info.bb}_${data.level_info.ante !== undefined && data.level_info.ante !== null ? data.level_info.ante : ''}`}
          onChange={(e) => {
            if (!e.target.value) return;
            const [sb, bb, ante] = e.target.value.split('_');
            loadData(
              currentCategory,
              parseFloat(sb),
              parseFloat(bb),
              ante !== '' ? parseFloat(ante) : null
            );
          }}
          disabled={loadingLevels || availableLevels.length === 0}
          style={{
            background: '#1a1f2b', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 6, fontSize: 14, fontWeight: 600, textAlign: 'center',
            width: 'auto', minWidth: 150, flex: '0 1 240px',
          }}
        >
          {availableLevels.map((level, idx) => (
            <option key={idx} value={`${level.sb}_${level.bb}_${(level.ante !== undefined && level.ante !== null ? level.ante : '')}`}>
              {level.display}
            </option>
          ))}
        </Input>
        <Button
          size="sm"
          onClick={() => switchLevel(1)}
          disabled={availableLevels.length === 0}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
                   color: '#fff', borderRadius: 6, padding: '6px 11px' }}
          title="下一個級別"
        >
          <i className="fa fa-chevron-right" />
        </Button>

        <Button
          size="sm"
          outline
          color="secondary"
          style={{ marginLeft: 'auto' }}
          onClick={() => {
            setData(null);
            setSearchForm({
              category: currentCategory,
              sb: data.level_info.sb,
              bb: data.level_info.bb,
              ante: data.level_info.ante || ''
            });
          }}
        >
          <i className="fa fa-search mr-1" />重新搜尋
        </Button>
      </div>

      {/* 🔑 這個類別在所選週期沒有資料時：**留在這一頁、tab 也留在這個類別**，
             只把數據區換成警示。`data` 沒有被清掉，所以點回原本的類別
             會立刻還原（不用重查）—— Brad 2026-09-10：「怎麼樣都不要回到
             Initial 的那個頁面，然後才給 Warning」。 */}
      {emptyCategory ? (
        <Card style={{ background: COLORS.cardBg, border: '1px solid rgba(251,99,64,0.45)' }}>
          <CardBody className="text-center" style={{ padding: '44px 20px' }}>
            <i className="fa fa-triangle-exclamation fa-2x mb-3"
               style={{ display: 'block', color: COLORS.warning }} />
            <div style={{ color: COLORS.textPrimary, fontWeight: 600, marginBottom: 6 }}>
              {GAME_TYPES[emptyCategory]?.name || emptyCategory} 在所選週期沒有任何級別資料
            </div>
            <div style={{ color: COLORS.textMuted, fontSize: 13 }}>
              可以改選其他遊戲類別，或在上方調整週期後再看一次。
            </div>
          </CardBody>
        </Card>
      ) : (
      <>
      {/* 對比摘要：真人 / EcoBot / 總計 —— 三個同層級的面板 */}
      <Row>
        {[
          {
            key: 'real', accent: '#667eea', title: '真人玩家',
            value: summary.real.rake, prev: prevSummary ? Number(prevSummary.real?.rake || 0) : undefined,
            deltaLabel: 'Rake',
            detail: (
              <>
                <i className="fa fa-users" /> {numberWithCommas(summary.real.players)} 玩家
                {' · '}<i className="fa fa-table" /> {numberWithCommas(summary.real.tables)} 桌
                {' · '}<i className="fa fa-hand" /> {numberWithCommas(summary.real.hands)} 手
              </>
            ),
          },
          {
            key: 'ecobot', accent: '#00d6b4', title: 'EcoBot',
            value: summary.ecobot.rake, prev: prevSummary ? Number(prevSummary.ecobot?.rake || 0) : undefined,
            deltaLabel: 'Rake', valueColor: '#00d6b4',
            detail: (
              <>
                <i className="fa fa-users" /> {numberWithCommas(summary.ecobot.players)} 玩家
                {' · '}<i className="fa fa-table" /> {numberWithCommas(summary.ecobot.tables)} 桌
                {' · '}<i className="fa fa-hand" /> {numberWithCommas(summary.ecobot.hands)} 手
              </>
            ),
          },
          {
            key: 'total', accent: '#11cdef', title: '總計',
            value: summary.total.rake, prev: prevSummary ? Number(prevSummary.total?.rake || 0) : undefined,
            deltaLabel: 'Rake（較上一期）',
            detail: (
              <>
                真人佔比
                <Badge color="info" style={{ fontSize: '0.95em', marginLeft: 6 }}>
                  {calculatePercentage(summary.real.rake, summary.total.rake)}%
                </Badge>
              </>
            ),
          },
        ].map((c) => (
          <Col lg="4" md="6" className="mb-3" key={c.key}>
            <div style={{
              background: '#1e2530',
              border: '1px solid rgba(255,255,255,0.1)',
              borderTop: `2px solid ${c.accent}`,
              borderRadius: 10, padding: '13px 16px', height: '100%',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, fontWeight: 500 }}>{c.title}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ color: c.valueColor || '#fff', fontWeight: 700, fontSize: '1.6rem', lineHeight: 1.1 }}>
                  ${numberWithCommas(c.value)}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                  {c.deltaLabel}{' '}
                  <GrowthBadge current={Number(c.value || 0)} previous={c.prev} />
                </span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 'auto' }}>{c.detail}</div>
            </div>
          </Col>
        ))}
      </Row>

      {/* 詳細對比表格 */}
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                <i className="fa fa-balance-scale mr-2"></i>
                真人 vs EcoBot 對比
              </CardTitle>
            </CardHeader>
            <CardBody>
              <Table responsive className="table-hover">
                <thead className="text-primary">
                  <tr>
                    <th>類型</th>
                    <th className="text-right">Rake</th>
                    <th className="text-right">P&L (水後)</th>
                    <th className="text-right">水前輸贏</th>
                    <th className="text-right">JP FEE</th>
                    <th className="text-right">JP PAYOUT</th>
                    <th className="text-right">水前輸贏扣除JP</th>
                    <th className="text-right">手數</th>
                    <th className="text-right">活躍人數</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{backgroundColor: 'rgba(31, 142, 241, 0.1)'}}>
                    <td><strong style={{color: '#1f8ef1'}}>真人</strong></td>
                    <td className="text-right">${numberWithCommas(summary.real.rake)}</td>
                    <td className="text-right" style={{color: summary.real.pl >= 0 ? '#00d6b4' : '#fd5d93'}}>
                      ${numberWithCommas(summary.real.pl)}
                    </td>
                    <td className="text-right">${numberWithCommas(summary.real.pre_rake_pl)}</td>
                    <td className="text-right">${numberWithCommas(summary.real.jp_fee)}</td>
                    <td className="text-right" style={{color: '#00d6b4'}}>
                      ${numberWithCommas(summary.real.jp_payout)}
                    </td>
                    <td className="text-right" style={{
                      color: summary.real.pre_rake_pl_exclude_jp >= 0 ? '#00d6b4' : '#fd5d93',
                      fontWeight: '600'
                    }}>
                      ${numberWithCommas(summary.real.pre_rake_pl_exclude_jp)}
                    </td>
                    <td className="text-right">{numberWithCommas(summary.real.hands)}</td>
                    <td className="text-right">{numberWithCommas(summary.real.players)}</td>
                  </tr>
                  <tr style={{backgroundColor: 'rgba(0, 214, 180, 0.1)'}}>
                    <td><strong style={{color: '#00d6b4'}}>EcoBot</strong></td>
                    <td className="text-right">${numberWithCommas(summary.ecobot.rake)}</td>
                    <td className="text-right" style={{color: summary.ecobot.pl >= 0 ? '#00d6b4' : '#fd5d93'}}>
                      ${numberWithCommas(summary.ecobot.pl)}
                    </td>
                    <td className="text-right">${numberWithCommas(summary.ecobot.pre_rake_pl)}</td>
                    <td className="text-right">${numberWithCommas(summary.ecobot.jp_fee)}</td>
                    <td className="text-right" style={{color: '#00d6b4'}}>
                      ${numberWithCommas(summary.ecobot.jp_payout)}
                    </td>
                    <td className="text-right" style={{
                      color: summary.ecobot.pre_rake_pl_exclude_jp >= 0 ? '#00d6b4' : '#fd5d93',
                      fontWeight: '600'
                    }}>
                      ${numberWithCommas(summary.ecobot.pre_rake_pl_exclude_jp)}
                    </td>
                    <td className="text-right">{numberWithCommas(summary.ecobot.hands)}</td>
                    <td className="text-right">{numberWithCommas(summary.ecobot.players)}</td>
                  </tr>
                  {/* v2.28.0 自營Bot：我方自己研發的開桌機器人（≠ EcoBot 外部夥伴）。
                      沒有資料時整列不顯示，免得每個級別都多一列 0。 */}
                  {summary.self_bot && summary.self_bot.hands > 0 && (
                  <tr style={{backgroundColor: 'rgba(225, 78, 202, 0.1)'}}>
                    <td><strong style={{color: '#e14eca'}}>自營Bot</strong></td>
                    <td className="text-right">${numberWithCommas(summary.self_bot.rake)}</td>
                    <td className="text-right" style={{color: summary.self_bot.pl >= 0 ? '#00d6b4' : '#fd5d93'}}>
                      ${numberWithCommas(summary.self_bot.pl)}
                    </td>
                    <td className="text-right">${numberWithCommas(summary.self_bot.pre_rake_pl)}</td>
                    <td className="text-right">${numberWithCommas(summary.self_bot.jp_fee)}</td>
                    <td className="text-right" style={{color: '#00d6b4'}}>
                      ${numberWithCommas(summary.self_bot.jp_payout)}
                    </td>
                    <td className="text-right" style={{
                      color: summary.self_bot.pre_rake_pl_exclude_jp >= 0 ? '#00d6b4' : '#fd5d93',
                      fontWeight: '600'
                    }}>
                      ${numberWithCommas(summary.self_bot.pre_rake_pl_exclude_jp)}
                    </td>
                    <td className="text-right">{numberWithCommas(summary.self_bot.hands)}</td>
                    <td className="text-right">{numberWithCommas(summary.self_bot.players)}</td>
                  </tr>
                  )}
                  <tr style={{borderTop: '2px solid rgba(255,255,255,0.2)', fontWeight: 'bold'}}>
                    <td>總計</td>
                    <td className="text-right">${numberWithCommas(summary.total.rake)}</td>
                    <td className="text-right" style={{color: summary.total.pl >= 0 ? '#00d6b4' : '#fd5d93'}}>
                      ${numberWithCommas(summary.total.pl)}
                    </td>
                    <td className="text-right">${numberWithCommas(summary.total.pre_rake_pl)}</td>
                    <td className="text-right">${numberWithCommas(summary.total.jp_fee)}</td>
                    <td className="text-right" style={{color: '#00d6b4'}}>
                      ${numberWithCommas(summary.total.jp_payout)}
                    </td>
                    <td className="text-right" style={{
                      color: summary.total.pre_rake_pl_exclude_jp >= 0 ? '#00d6b4' : '#fd5d93',
                      fontWeight: '600'
                    }}>
                      ${numberWithCommas(summary.total.pre_rake_pl_exclude_jp)}
                    </td>
                    <td className="text-right">{numberWithCommas(summary.total.hands)}</td>
                    <td className="text-right">{numberWithCommas(summary.total.players)}</td>
                  </tr>
                  <tr style={{opacity: 0.7, borderTop: '1px solid rgba(255,255,255,0.1)'}}>
                    <td>真人佔比</td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.rake, summary.total.rake)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.pl, summary.total.pl)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.pre_rake_pl, summary.total.pre_rake_pl)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.jp_fee, summary.total.jp_fee)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.jp_payout, summary.total.jp_payout)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.pre_rake_pl_exclude_jp, summary.total.pre_rake_pl_exclude_jp)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.hands, summary.total.hands)}%</Badge>
                    </td>
                    <td className="text-right">
                      <Badge color="info">{calculatePercentage(summary.real.players, summary.total.players)}%</Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
      {/* 趨勢圖表 */}
      {data.by_week && Object.keys(data.by_week).length > 1 && (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="3">
                    <CardTitle tag="h4" className="mb-0">
                      <i className="fa fa-line-chart mr-2"></i>
                      周度趨勢
                    </CardTitle>
                  </Col>
                  <Col md="9">
                    <div className="d-flex flex-column align-items-end">
                      <div className="mb-2">
                        <ButtonGroup size="sm">
                          <Button
                            color={trendMetrics.rake ? "info" : "secondary"}
                            onClick={() => setTrendMetrics({...trendMetrics, rake: !trendMetrics.rake})}
                          >
                            Rake
                          </Button>
                          <Button
                            color={trendMetrics.pl ? "info" : "secondary"}
                            onClick={() => setTrendMetrics({...trendMetrics, pl: !trendMetrics.pl})}
                          >
                            P&L
                          </Button>
                          <Button
                            color={trendMetrics.preRakePl ? "info" : "secondary"}
                            onClick={() => setTrendMetrics({...trendMetrics, preRakePl: !trendMetrics.preRakePl})}
                          >
                            水前輸贏
                          </Button>
                        </ButtonGroup>
                      </div>
                      <div>
                        <ButtonGroup size="sm">
                          <Button
                            color={trendMetrics.jpFee ? "warning" : "secondary"}
                            onClick={() => setTrendMetrics({...trendMetrics, jpFee: !trendMetrics.jpFee})}
                          >
                            JP FEE
                          </Button>
                          <Button
                            color={trendMetrics.jpPayout ? "success" : "secondary"}
                            onClick={() => setTrendMetrics({...trendMetrics, jpPayout: !trendMetrics.jpPayout})}
                          >
                            JP PAYOUT
                          </Button>
                          <Button
                            color={trendMetrics.preRakePlExcludeJp ? "primary" : "secondary"}
                            onClick={() => setTrendMetrics({...trendMetrics, preRakePlExcludeJp: !trendMetrics.preRakePlExcludeJp})}
                            style={{fontWeight: trendMetrics.preRakePlExcludeJp ? '600' : '400'}}
                          >
                            水前扣除JP
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <div style={{height: '400px'}}>
                  {generateTrendChartData() && (
                    <Line data={generateTrendChartData()} options={lineChartOptions} />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      </>
      )}
      </TinyLoadingOverlay>
    </div>
  );
}

export default TinyGameLevelDetail;
